import React, { useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";
import { Button } from "react-bootstrap";
import "./projectMappingDetails.css";

const getPincodeValue = (details) => {
  if (!details) return "";
  if (details.pin_code) return details.pin_code;
  if (details.geo_pincode && details.geo_pincode.pincode) {
    return details.geo_pincode.pincode;
  }
  if (details.geo_pin_code) return details.geo_pin_code;
  return "";
};

const getCityValue = (details) => {
  if (!details) return "";
  if (details.geo_city && details.geo_city.city_name) {
    return details.geo_city.city_name;
  }
  return "";
};

const getStateValue = (details) => {
  if (!details) return "";
  if (details.geo_state && details.geo_state.state_name) {
    return details.geo_state.state_name;
  }
  return "";
};

const formatAddress = (details) => {
  if (!details) return "-";
  const addressParts = [
    details.address_line_1,
    details.address_line_2,
    getPincodeValue(details),
  ]
    .filter((part) => part !== null && part !== undefined && part !== "")
    .map((part) => String(part).trim());

  return addressParts.length ? addressParts.join(", ") : "-";
};

const AddressOption = ({
  title,
  type,
  entity,
  selected,
  onSelect,
  onConfirm,
  registeredNameChanged,
  addressChanged,
  cityChanged,
  stateChanged,
  pincodeChanged,
}) => {
  const details = entity?.lead_address_details;

  if (!details) {
    return null;
  }

  return (
    <div className={`project-mapping-details__card ${selected === type ? 'project-mapping-details__card--selected' : ''}`}>
      <div className="project-mapping-details__card-header">
        <label className="project-mapping-details__checkbox">
          <input
            type="checkbox"
            name="projectAddressOption"
            value={type}
            checked={selected === type}
            onChange={() => onSelect(type, entity)}
          />
          <span className="project-mapping-details__checkbox-tick">
            {selected === type ? '✓' : ''}
          </span>
          <span className="project-mapping-details__checkbox-label">{title}</span>
        </label>
        {selected === type && (
          <Button
            className="btn btn-primary btn-sm project-mapping-details__confirm-button-header"
            onClick={() => onConfirm && onConfirm(type, entity)}
          >
            Confirm Address
          </Button>
        )}
      </div>
      <div className="project-mapping-details__card-body">
        <div className="project-mapping-details__row">
          <span className="project-mapping-details__label">Project Name :</span>
          <span
            className={"project-mapping-details__value"}
          >
            {entity?.client_name || "-"}
          </span>
        </div>
        <div className="project-mapping-details__row">
          <span className="project-mapping-details__label">Project Id :</span>
          <span
            className={"project-mapping-details__value"}
          >
            {entity?.infra_project_id || "-"}
          </span>
        </div>
        <div className="project-mapping-details__row">
          <span className="project-mapping-details__label">Registered Name :</span>
          <span
            className={"project-mapping-details__value"}
          >
            {details.registered_name || "-"}
          </span>
        </div>
        <div className="project-mapping-details__row">
          <span className="project-mapping-details__label">Address :</span>
          <span
            className={"project-mapping-details__value"}
          >
            {formatAddress(details)}
          </span>
        </div>
        <div className="project-mapping-details__row">
          <span className="project-mapping-details__label">City :</span>
          <span
            className={"project-mapping-details__value"}
          >
            {getCityValue(details) || "-"}
          </span>
        </div>
        <div className="project-mapping-details__row">
          <span className="project-mapping-details__label">State :</span>
          <span
            className={"project-mapping-details__value"}
          >
            {getStateValue(details) || "-"}
          </span>
        </div>
        <div className="project-mapping-details__row">
          <span className="project-mapping-details__label">Pincode :</span>
          <span
            className={"project-mapping-details__value"}
          >
            {getPincodeValue(details) || "-"}
          </span>
        </div>
      </div>
    </div>
  );
};

AddressOption.propTypes = {
  title: PropTypes.string.isRequired,
  type: PropTypes.oneOf(["old", "new"]).isRequired,
  entity: PropTypes.object,
  selected: PropTypes.oneOf(["old", "new"]),
  onSelect: PropTypes.func.isRequired,
  onConfirm: PropTypes.func,
  registeredNameChanged: PropTypes.bool.isRequired,
  addressChanged: PropTypes.bool.isRequired,
  cityChanged: PropTypes.bool.isRequired,
  stateChanged: PropTypes.bool.isRequired,
  pincodeChanged: PropTypes.bool.isRequired,
};

const ProjectMappingDetails = ({ data, onAddressChange, onConfirm }) => {
  const oldEntity =
    data?.old || (data?.old_lead_address_details && {
      lead_address_details: data.old_lead_address_details,
    }) || null;
  const newEntity =
    data?.current || (data?.current_lead_address_details && {
      lead_address_details: data.current_lead_address_details,
    }) || null;

  const oldAddress = oldEntity?.lead_address_details || null;
  const newAddress = newEntity?.lead_address_details || null;

  const [selected, setSelected] = useState(
    newAddress ? "new" : oldAddress ? "old" : null
  );

  useEffect(() => {
    if (newAddress && newEntity) {
      setSelected("new");
      if (onAddressChange) {
        onAddressChange("new", newEntity);
      }
    } else if (oldAddress && oldEntity) {
      setSelected("old");
      if (onAddressChange) {
        onAddressChange("old", oldEntity);
      }
    } else {
      setSelected(null);
      if (onAddressChange) {
        onAddressChange(null, null);
      }
    }
  }, [oldAddress, newAddress, oldEntity, newEntity, onAddressChange]);

  const {
    registeredNameChanged,
    addressChanged,
    cityChanged,
    stateChanged,
    pincodeChanged,
  } = useMemo(() => {
    if (!oldAddress || !newAddress) {
      return {
        registeredNameChanged: false,
        addressChanged: false,
        cityChanged: false,
        stateChanged: false,
        pincodeChanged: false,
      };
    }

    const oldAddressString = formatAddress(oldAddress);
    const newAddressString = formatAddress(newAddress);
    const oldCity = getCityValue(oldAddress);
    const newCity = getCityValue(newAddress);
    const oldState = getStateValue(oldAddress);
    const newState = getStateValue(newAddress);
    const oldPincode = getPincodeValue(oldAddress);
    const newPincode = getPincodeValue(newAddress);

    return {
      registeredNameChanged:
        (oldAddress.registered_name || "") !== (newAddress.registered_name || ""),
      addressChanged: oldAddressString !== newAddressString,
      cityChanged: oldCity !== newCity,
      stateChanged: oldState !== newState,
      pincodeChanged: oldPincode !== newPincode,
    };
  }, [oldAddress, newAddress]);

  const handleSelect = (type, entity) => {
    // Select this one (deselects the other automatically)
    setSelected(type);
    if (onAddressChange) {
      onAddressChange(type, entity?.lead_address_details ? entity : null);
    }
  };

  if (!oldAddress && !newAddress) {
    return (
      <div className="project-mapping-details__empty">
        No address data available.
      </div>
    );
  }

  return (
    <div className="project-mapping-details">
      <AddressOption
        title="Old Address"
        type="old"
        entity={oldEntity}
        selected={selected}
        onSelect={handleSelect}
        onConfirm={onConfirm}
        registeredNameChanged={registeredNameChanged}
        addressChanged={addressChanged}
        cityChanged={cityChanged}
        stateChanged={stateChanged}
        pincodeChanged={pincodeChanged}
      />
      <AddressOption
        title="New Address"
        type="new"
        entity={newEntity}
        selected={selected}
        onSelect={handleSelect}
        onConfirm={onConfirm}
        registeredNameChanged={registeredNameChanged}
        addressChanged={addressChanged}
        cityChanged={cityChanged}
        stateChanged={stateChanged}
        pincodeChanged={pincodeChanged}
      />
    </div>
  );
};

ProjectMappingDetails.propTypes = {
  data: PropTypes.shape({
    old_lead_address_details: PropTypes.object,
    current_lead_address_details: PropTypes.object,
    old: PropTypes.object,
    current: PropTypes.object,
  }),
  onAddressChange: PropTypes.func,
  onConfirm: PropTypes.func,
};

ProjectMappingDetails.defaultProps = {
  data: null,
  onAddressChange: undefined,
};

export default ProjectMappingDetails;

